/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-empty-function */
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import React, { createContext, FC, useState, useEffect } from 'react';
import useFetchState from '../hooks/useFetchState';
import { IReturnLabel } from '../models/returns';
import {
  IErrorOMSService,
  IErrorReturnService,
  IPrintLabelError,
} from '../models/returns/returns-common';
import { buildReturnLabelRequest } from '../utils/returns/returns-helper';
import {
  createHtmlContent,
  getErrorBySSRCode,
  openNativeAppLabel,
  openNewWindow,
} from '../utils/returns/return-label';
import { useExperienceContext } from '../hooks/useContexts';
import { GetReturnShippingLabelAPI } from '../utils/returns/api-helpers';

interface IReturnLabelProvider {
  children: React.ReactNode;
  orderId: string;
  emailId: string;
  trackingNumbers: string[];
  isGuest: boolean;
  net: string | undefined;
  xAuthGuestToken?: string;
}

type ButtonClickedType = 'barcode' | 'label' | '';

export interface IReturnLabelContext {
  orderId: string;
  loading: boolean;
  fetchReturnLabel: (
    type: ButtonClickedType,
    openWindow?: boolean,
  ) => Promise<void>;
  returnLabelResponse: IReturnLabel[] | null;
  error: IErrorReturnService | IErrorOMSService | Error | null;
  apiError: IPrintLabelError | undefined;
  setAPIError: (error: IPrintLabelError | undefined) => void;
  retryAvailable: boolean;
  setRetryAvailable: (value: boolean) => void;
  openNewReturnLabelWindow: () => void;
  lastClicked: ButtonClickedType;
}

const defaultState: IReturnLabelContext = {
  orderId: '',
  loading: false,
  error: null,
  lastClicked: '',
  fetchReturnLabel: async () => {},
  returnLabelResponse: null,
  apiError: undefined,
  setAPIError: () => {},
  retryAvailable: true,
  setRetryAvailable: () => {},
  openNewReturnLabelWindow: () => {},
};

export const ReturnLabelContext =
  createContext<IReturnLabelContext>(defaultState);

export const ReturnLabelProvider: FC<IReturnLabelProvider> = ({
  children,
  orderId,
  emailId,
  trackingNumbers,
  isGuest,
  net,
  xAuthGuestToken,
}) => {
  const {
    loading,
    fetchData,
    data: returnLabelResponse,
    error,
  } = useFetchState<IReturnLabel[]>();
  const [retryAvailable, setRetryAvailable] = useState(true);
  const [apiError, setAPIError] = useState<IPrintLabelError | undefined>();
  const [lastClicked, setLastClicked] = useState<ButtonClickedType>('');
  const { isConsumerApp } = useExperienceContext();
  const { userID } = useThdCustomer();
  const isNativeApp = isConsumerApp || false;

  const request = buildReturnLabelRequest(
    orderId,
    emailId,
    trackingNumbers,
    isGuest,
    net,
  );

  const fetchReturnLabel = async (
    type: ButtonClickedType,
    openWindow?: boolean,
  ) => {
    setLastClicked(type);
    setAPIError(undefined);
    let labelWindow: Window | null = null;
    if (!isNativeApp && openWindow) {
      labelWindow = openNewReturnLabelWindow();
      if (labelWindow === null) return;
    }

    if (!returnLabelResponse) {
      const { data, error: fetchError } = await fetchData(() =>
        GetReturnShippingLabelAPI(request, userID, xAuthGuestToken),
      );
      if (data) {
        setRetryAvailable(true);
        if (type === 'barcode') return;
        const htmlLabel = createHtmlContent(data, orderId);
        if (isNativeApp) {
          return openNativeAppLabel(htmlLabel);
        }
        if (labelWindow) {
          return labelWindow.document.write(htmlLabel || '');
        }
      }
      // on failure
      if (labelWindow) {
        labelWindow.close();
      }
      if (fetchError) {
        setRetryAvailable(false);
        if ('code' in fetchError) {
          const errorInfo = getErrorBySSRCode(
            fetchError as IErrorReturnService,
          );
          setAPIError(errorInfo);
          return;
        }
        setAPIError({ type: 'TRYAGAIN', retryable: true });
      }
    } else {
      setRetryAvailable(true);
      if (type === 'barcode') return;
      const htmlLabel = createHtmlContent(returnLabelResponse, orderId);
      if (isNativeApp) {
        return openNativeAppLabel(htmlLabel);
      }
      if (labelWindow) {
        return labelWindow.document.write(htmlLabel || '');
      }
    }
  };

  const openNewReturnLabelWindow = () => {
    const newWindow = openNewWindow();
    if (!newWindow) {
      setAPIError({ type: 'TRYAGAIN', retryable: true });
      return null;
    }
    return newWindow;
  };

  useEffect(() => {
    if (apiError?.retryable) {
      const timeoutId = setTimeout(() => {
        setRetryAvailable(true);
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [apiError?.retryable, retryAvailable]);

  return (
    <ReturnLabelContext.Provider
      value={{
        orderId,
        loading,
        lastClicked,
        fetchReturnLabel,
        returnLabelResponse,
        apiError,
        setAPIError,
        error,
        retryAvailable,
        setRetryAvailable,
        openNewReturnLabelWindow,
      }}
    >
      {children}
    </ReturnLabelContext.Provider>
  );
};
